import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api, type Paginated } from "@/lib/api";

/**
 * Cursor-paginated list state, with filters mirrored into the URL.
 *
 * Putting filters in the query string means an admin can share a link to
 * "suspended users in Indore" and it opens with the same view, and the back
 * button behaves the way a browser should.
 */

export interface ListState {
  q: string;
  sort?: string;
  order: "asc" | "desc";
  filters: Record<string, string | undefined>;
}

export const useListQuery = <T,>(opts: {
  key: string;
  path: string;
  /** Filter names read from and written to the URL. */
  filterKeys: readonly string[];
  defaultSort?: string;
  defaultOrder?: "asc" | "desc";
  defaultFilters?: Record<string, string>;
  limit?: number;
  enabled?: boolean;
}) => {
  const {
    key,
    path,
    filterKeys,
    defaultSort,
    defaultOrder = "desc",
    defaultFilters,
    limit = 50,
    enabled = true,
  } = opts;

  const [params, setParams] = useSearchParams();

  const q = params.get("q") ?? "";
  const sort = params.get("sort") ?? defaultSort;
  const order = (params.get("order") as "asc" | "desc") ?? defaultOrder;

  const filters = React.useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const k of filterKeys) {
      out[k] = params.get(k) ?? defaultFilters?.[k] ?? undefined;
    }
    return out;
  }, [params, filterKeys, defaultFilters]);

  const patch = React.useCallback(
    (updates: Record<string, string | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === undefined || v === "") next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setQ = React.useCallback((value: string) => patch({ q: value }), [patch]);

  const setSort = React.useCallback(
    (field: string, dir: "asc" | "desc") => patch({ sort: field, order: dir }),
    [patch],
  );

  const setFilter = React.useCallback(
    (name: string, value: string | undefined) => patch({ [name]: value }),
    [patch],
  );

  const clearFilters = React.useCallback(() => {
    const cleared: Record<string, undefined> = {};
    for (const k of filterKeys) cleared[k] = undefined;
    patch({ ...cleared, q: undefined });
  }, [filterKeys, patch]);

  const queryParams = { q: q || undefined, sort, order, limit, ...filters };

  const query = useInfiniteQuery({
    queryKey: [key, queryParams],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<T>>(
        path,
        { ...queryParams, cursor: pageParam },
        signal,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  const total = query.data?.pages[0]?.total ?? 0;
  const nextCursor = query.hasNextPage ? "more" : null;

  return {
    rows,
    total,
    nextCursor,
    loading: query.isLoading,
    loadingMore: query.isFetchingNextPage,
    error: query.error,
    loadMore: () => void query.fetchNextPage(),
    refetch: query.refetch,

    q,
    setQ,
    sort,
    order,
    setSort,
    filters,
    setFilter,
    clearFilters,
    activeFilterCount:
      Object.values(filters).filter(Boolean).length + (q ? 1 : 0),
  };
};
